/**
 * 페이지·크기·문자열 상한(§6.4 공통 규칙). 계약 값은 `mcp-tools.json` 한 곳에서 온다.
 */
import catalog from "../mcp-tools.json";

export const LIMITS = catalog.limits;

/** 응답 봉투(ok·meta)가 들어갈 여유. 목록은 이만큼 덜 채운다. */
const ENVELOPE_MARGIN = 16 * 1024;

export class ToolFailure extends Error {
  constructor(
    readonly code: "NOT_FOUND" | "INVALID_ARGUMENT" | "INTERNAL",
    message: string,
  ) {
    super(message);
  }
}

/** 자유 텍스트는 필드당 2,000자(ADV-F09). 잘리면 끝에 "…". */
export function clip(text: string | undefined | null, max: number = LIMITS.maxTextChars): string | undefined {
  if (text == null) return undefined;
  const chars = [...text];
  if (chars.length <= max) return text;
  return `${chars.slice(0, max - 1).join("")}…`;
}

export function readLimit(args: Record<string, unknown>): number {
  const n = args.limit;
  if (n === undefined) return LIMITS.defaultLimit;
  if (typeof n !== "number" || !Number.isInteger(n) || n < 1) throw new ToolFailure("INVALID_ARGUMENT", "limit: 1 이상의 정수여야 합니다");
  return Math.min(n, LIMITS.maxLimit);
}

export function readOffset(args: Record<string, unknown>): number {
  const c = args.cursor;
  if (c === undefined) return 0;
  if (typeof c !== "string" || !/^\d{1,9}$/.test(c)) throw new ToolFailure("INVALID_ARGUMENT", "cursor: 이전 응답의 nextCursor를 그대로 주세요");
  return Number(c);
}

const encoder = new TextEncoder();
export function byteLength(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length;
}

export interface Page<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
  /** 개수가 아니라 크기 상한 때문에 덜 담았다 */
  truncated: boolean;
}

/**
 * `all`에서 cursor·limit만큼 꺼내 `map`한다. 직렬화 크기가 상한(1 MiB)을 넘기 전에 멈추고
 * `truncated:true`와 이어 받을 `nextCursor`를 준다(ADV-E09). 항목이 하나도 안 들어가면 첫 항목만 담는다.
 */
export function page<S, T>(all: readonly S[], args: Record<string, unknown>, map: (s: S) => T, budget = LIMITS.maxResultBytes - ENVELOPE_MARGIN): Page<T> {
  const limit = readLimit(args);
  const offset = readOffset(args);
  const items: T[] = [];
  let used = 64;
  let truncated = false;
  let i = offset;
  for (; i < all.length && items.length < limit; i++) {
    const item = map(all[i]!);
    const size = byteLength(item) + 1;
    if (used + size > budget && items.length > 0) {
      truncated = true;
      break;
    }
    items.push(item);
    used += size;
  }
  return { items, total: all.length, nextCursor: i < all.length ? String(i) : null, truncated };
}

/** 페이지 없이 크기 상한까지만 담는다(주 경로처럼 커서가 없는 목록). */
export function fit<S, T>(all: readonly S[], map: (s: S) => T, maxItems = Number.POSITIVE_INFINITY, budget = LIMITS.maxResultBytes - ENVELOPE_MARGIN): { items: T[]; truncated: boolean } {
  const items: T[] = [];
  let used = 64;
  for (const s of all) {
    if (items.length >= maxItems) return { items, truncated: true };
    const item = map(s);
    const size = byteLength(item) + 1;
    if (used + size > budget && items.length > 0) return { items, truncated: true };
    items.push(item);
    used += size;
  }
  return { items, truncated: false };
}

/** 공백으로 나눈 모든 단어가 들어 있으면 참(명령 팔레트와 같은 규칙). */
export function tokensMatch(query: string, text: string): boolean {
  const hay = text.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => hay.includes(t));
}
